/**
 * Signup device detection for usersNew.leadData.mobile and runtime diagnostics.
 *
 * Keep the legacy boolean mobile field for existing dashboards, while also
 * storing a normalized runtime classification for future cohort comparisons.
 */

export const SIGNUP_DEVICE_CLASSIFICATIONS = Object.freeze({
  PHONE: "phone",
  TABLET: "tablet",
  DESKTOP: "desktop",
  UNKNOWN: "unknown",
});

const MOBILE_UA_PATTERN = /Mobi|Android.*Mobile|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i;
const TABLET_UA_PATTERN = /iPad|Tablet|Android(?!.*Mobile)|Silk|Kindle|PlayBook/i;
const IOS_DEVICE_UA_PATTERN = /iPhone|iPad|iPod/i;
const ANDROID_UA_PATTERN = /Android/i;
const FACEBOOK_IN_APP_PATTERN = /\bFBAN|FBAV|Instagram|Line\/|Twitter|TikTok|Pinterest/i;
const ROTATE_GATE_MAX_WIDTH = 900;

function getWindowLike(options = {}) {
  return options.window || (typeof window !== "undefined" ? window : null);
}

function getNavigatorLike(options = {}) {
  return options.navigator || (typeof navigator !== "undefined" ? navigator : null);
}

function getScreenLike(options = {}) {
  const win = getWindowLike(options);
  return options.screen || win?.screen || (typeof screen !== "undefined" ? screen : null);
}

function readMediaQuery(win, query, fallback = false) {
  try {
    return Boolean(win?.matchMedia?.(query)?.matches);
  } catch (error) {
    return fallback;
  }
}

function clampDimension(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? Math.round(numericValue) : null;
}

function resolveBrowserFamily(userAgent = "") {
  if (/Edg\//i.test(userAgent)) return "edge";
  if (/CriOS|Chrome\//i.test(userAgent)) return "chrome";
  if (/Firefox|FxiOS/i.test(userAgent)) return "firefox";
  if (/Safari\//i.test(userAgent) && !/Chrome|CriOS|Android/i.test(userAgent)) return "safari";
  return "unknown";
}

function resolveOsFamily(userAgent = "") {
  if (IOS_DEVICE_UA_PATTERN.test(userAgent)) return "ios";
  if (ANDROID_UA_PATTERN.test(userAgent)) return "android";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "macos";
  if (/Windows/i.test(userAgent)) return "windows";
  if (/Linux/i.test(userAgent)) return "linux";
  return "unknown";
}

export function getRuntimeDeviceSignals(options = {}) {
  const nav = getNavigatorLike(options);
  const win = getWindowLike(options);
  const screenLike = getScreenLike(options);
  const userAgent = String(options.userAgent ?? nav?.userAgent ?? "");
  const viewportWidth = clampDimension(options.viewportWidth ?? win?.innerWidth);
  const viewportHeight = clampDimension(options.viewportHeight ?? win?.innerHeight);
  const screenWidth = clampDimension(options.screenWidth ?? screenLike?.width);
  const screenHeight = clampDimension(options.screenHeight ?? screenLike?.height);
  const maxTouchPoints = Number(options.maxTouchPoints ?? nav?.maxTouchPoints ?? 0) || 0;
  const pointerCoarse = typeof options.pointerCoarse === "boolean"
    ? options.pointerCoarse
    : readMediaQuery(win, "(pointer: coarse)", false);
  const orientationPortrait = typeof options.orientationPortrait === "boolean"
    ? options.orientationPortrait
    : (viewportWidth && viewportHeight ? viewportHeight >= viewportWidth : readMediaQuery(win, "(orientation: portrait)", false));

  return {
    userAgent: userAgent.slice(0, 500),
    userAgentDataMobile: typeof nav?.userAgentData?.mobile === "boolean" ? nav.userAgentData.mobile : null,
    viewportWidth,
    viewportHeight,
    screenWidth,
    screenHeight,
    maxTouchPoints,
    pointerCoarse,
    orientationPortrait,
    osFamily: resolveOsFamily(userAgent),
    browserFamily: resolveBrowserFamily(userAgent),
    isInAppBrowser: FACEBOOK_IN_APP_PATTERN.test(userAgent),
  };
}

export function classifyRuntimeDevice(options = {}) {
  const signals = getRuntimeDeviceSignals(options);
  const minViewportSide = Math.min(
    signals.viewportWidth || Number.POSITIVE_INFINITY,
    signals.viewportHeight || Number.POSITIVE_INFINITY
  );
  const minScreenSide = Math.min(
    signals.screenWidth || Number.POSITIVE_INFINITY,
    signals.screenHeight || Number.POSITIVE_INFINITY
  );
  const hasTouch = signals.maxTouchPoints > 0 || signals.pointerCoarse;
  const uaMobile = MOBILE_UA_PATTERN.test(signals.userAgent);
  const uaTablet = TABLET_UA_PATTERN.test(signals.userAgent);
  const clientHintMobile = signals.userAgentDataMobile;
  const smallViewport = Number.isFinite(minViewportSide) && minViewportSide <= 600;
  const smallScreen = Number.isFinite(minScreenSide) && minScreenSide <= 720;
  const rotateGateWidthCandidate =
    (signals.viewportWidth && signals.viewportWidth <= ROTATE_GATE_MAX_WIDTH)
    || (signals.screenWidth && signals.screenWidth <= ROTATE_GATE_MAX_WIDTH);

  let device = SIGNUP_DEVICE_CLASSIFICATIONS.UNKNOWN;
  if (clientHintMobile === true || uaMobile || (hasTouch && smallViewport && smallScreen)) {
    device = SIGNUP_DEVICE_CLASSIFICATIONS.PHONE;
  } else if (uaTablet || (hasTouch && !smallViewport && Number.isFinite(minScreenSide) && minScreenSide <= 1024)) {
    device = SIGNUP_DEVICE_CLASSIFICATIONS.TABLET;
  } else if (clientHintMobile === false || !hasTouch || signals.viewportWidth >= 901) {
    device = SIGNUP_DEVICE_CLASSIFICATIONS.DESKTOP;
  }

  return {
    device,
    signals,
    isRotateGateCandidate: Boolean(
      device === SIGNUP_DEVICE_CLASSIFICATIONS.PHONE
      && signals.orientationPortrait
      && signals.pointerCoarse
      && rotateGateWidthCandidate
    ),
  };
}

export function detectSignupIsMobile(options = {}) {
  const nav = getNavigatorLike(options);
  if (!nav) return null;

  if (typeof nav.userAgentData?.mobile === "boolean") {
    return nav.userAgentData.mobile;
  }

  return classifyRuntimeDevice(options).device === SIGNUP_DEVICE_CLASSIFICATIONS.PHONE;
}

/**
 * Value to persist on leadData.mobile. Always a boolean when a navigator exists.
 */
export function getSignupMobileLeadValue(options = {}) {
  const detected = detectSignupIsMobile(options);
  return detected === null ? "" : detected;
}

export function getSignupRuntimeDeviceLeadFields(options = {}) {
  const classification = classifyRuntimeDevice(options);
  return {
    runtimeSignupDevice: classification.device,
    deviceSignals: classification.signals,
  };
}
